import React, { useEffect, useState } from "react";
import ThreatPanel from "../components/ThreatPanel";
import { AlertTriangle, Download, FileText, Loader2, ShieldCheck, ShieldX, UserCheck, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { RiskBadge, VerifyBadge } from "../components/SubjectRegistry";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FORM_LABEL = {
  dd_persona_natural: "nav.dd_natural",
  dd_persona_juridica: "nav.dd_juridica",
  declaracion_fondos: "nav.declaracion",
  certificacion_beneficiario: "nav.certificacion",
  dd_pn_aseguradora: "nav.dd_aseguradora",
  dd_pj_aseguradora: "nav.pj_aseguradora",
  carta_responsabilidad: "nav.carta",
};

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showClients, setShowClients] = useState(false);
  const [client, setClient] = useState(null);

  const openDetail = async (id) => {
    setDetail({ loading: true });
    try {
      const { data: d } = await api.get(`/sanctions/searches/${id}`);
      setDetail(d);
    } catch {
      setDetail(null);
    }
  };

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-slate-500" data-testid="dashboard-loading">{t("common.loading")}</div>;

  const kpis = [
    { label: t("dash.total_searches"), value: data.total_searches, icon: ShieldCheck, color: "text-[#044CBC] bg-orange-50", testid: "kpi-searches", to: "/historial" },
    { label: t("dash.matches"), value: data.matches, icon: ShieldX, color: "text-red-600 bg-red-50", testid: "kpi-matches", to: "/historial" },
    { label: t("dash.cleared"), value: data.cleared, icon: ShieldCheck, color: "text-emerald-600 bg-emerald-50", testid: "kpi-cleared", to: "/historial" },
    { label: t("dash.total_forms"), value: data.total_forms, icon: FileText, color: "text-indigo-700 bg-indigo-50", testid: "kpi-forms", to: "/historial" },
    { label: t("dash.users"), value: data.users_count, icon: Users, color: "text-slate-700 bg-slate-100", testid: "kpi-users", to: "/usuarios" },
    { label: t("dash.clients"), value: data.clients_count ?? 0, icon: UserCheck, color: "text-[#033A8C] bg-[#033A8C]/10", testid: "kpi-clients", action: () => setShowClients((v) => !v) },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("dash.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("dash.welcome")} — {user?.tenant_name}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpis.map((k) => (
          <button key={k.label} onClick={() => (k.action ? k.action() : navigate(k.to))} className="ec-card p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" data-testid={k.testid}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${k.color}`}>
              <k.icon className="w-5 h-5" />
            </div>
            <div className="font-heading text-3xl font-extrabold text-slate-900">{k.value}</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mt-1">{k.label}</div>
          </button>
        ))}
      </div>

      {["admin", "operator", "auditor"].includes(user?.role) && <ThreatPanel />}

      {showClients && (
        <div className="ec-card p-6" data-testid="clients-panel">
          <h3 className="font-heading font-bold text-slate-900 mb-4">{t("dash.clients")}</h3>
          {(data.clients || []).length === 0 && <p className="text-sm text-slate-400">{t("dash.no_data")}</p>}
          <ul className="divide-y divide-slate-100">
            {(data.clients || []).map((c) => (
              <li key={c.id}>
                <button onClick={() => setClient(c)} data-testid={`dash-client-${c.id}`} className="w-full flex flex-wrap items-center justify-between gap-2 text-sm rounded-lg px-2 py-2.5 hover:bg-slate-50 transition-colors text-left">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{c.name}</div>
                    <div className="text-xs text-slate-400 font-mono">{c.client_id || c.id_number || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <VerifyBadge verification={c.verification} t={t} />
                    <RiskBadge level={c.risk?.level} t={t} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="ec-card p-6 lg:col-span-1" data-testid="lists-status-card">
          <h3 className="font-heading font-bold text-slate-900 mb-1">{t("dash.lists")}</h3>
          <p className="text-xs text-slate-500 mb-4">{t("search.auto_note")}</p>
          {["OFAC", "UN", "FATF", "FBI", "INTERPOL", "WB", "NAMUS"].map((src) => {
            const s = data.sanctions_status?.[src] || {};
            const ok = s.status === "ok";
            return (
              <div key={src} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`list-status-${src}`}>
                <div>
                  <div className="text-sm font-bold text-slate-800">{{ OFAC: "OFAC (SDN)", UN: "CSNU (ONU)", FATF: "GAFI (FATF)", FBI: "FBI Most Wanted", INTERPOL: "INTERPOL", WB: "Banco Mundial", NAMUS: "NamUs" }[src]}</div>
                  <div className="text-xs text-slate-500">
                    {(s.count || 0).toLocaleString()} {t("search.entries")} · {t("search.last_update")}: {s.last_update ? fmtDate(s.last_update) : "—"}
                  </div>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {ok ? t("search.status_ok") : s.status === "error" ? t("search.status_error") : t("search.status_pending")}
                </span>
              </div>
            );
          })}
          {data.sanctions_status?.OFAC?.status === "error" && (
            <div className="flex items-start gap-2 mt-3 text-xs text-amber-700 bg-amber-50 rounded-md p-2.5">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {data.sanctions_status.OFAC.error}
            </div>
          )}
        </div>

        <div className="ec-card p-6 lg:col-span-1" data-testid="recent-searches-card">
          <h3 className="font-heading font-bold text-slate-900 mb-2">{t("dash.month_by_source")}</h3>
          <p className="text-[10px] text-slate-400 mb-3">{t("dash.month_since")} {data.month_start}</p>
          <div className="grid grid-cols-2 gap-1.5 mb-5" data-testid="month-by-source">
            {[["OFAC", "OFAC"], ["UN", "CSNU"], ["FATF", "GAFI"], ["FBI", "FBI"], ["INTERPOL", "INTERPOL"], ["WB", t("nav.wb")], ["NAMUS", "NamUs"], ["PEP", t("nav.pep")], ["OSINT", "OSINT"], ["KEV_NTAS_REPORTS", "Cyber/NTAS"]].map(([k, lbl]) => (
              <div key={k} className="flex items-center justify-between rounded-md bg-slate-50 px-2.5 py-1.5" data-testid={`month-src-${k}`}>
                <span className="text-[11px] font-semibold text-slate-600 truncate">{lbl}</span>
                <span className="font-heading text-sm font-extrabold text-slate-900 tabular-nums">{data.month_by_source?.[k] || 0}</span>
              </div>
            ))}
          </div>
          <h3 className="font-heading font-bold text-slate-900 mb-4">{t("dash.recent_searches")}</h3>
          {data.recent_searches.length === 0 && <p className="text-sm text-slate-400">{t("dash.no_data")}</p>}
          <ul className="space-y-1.5">
            {data.recent_searches.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => openDetail(s.id)}
                  data-testid={`recent-search-${s.consult_number}`}
                  className="w-full flex items-center justify-between gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-slate-100 transition-colors cursor-pointer text-left"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{s.query}</div>
                    <div className="text-xs text-slate-400 font-mono">{s.consult_number}</div>
                  </div>
                  <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full ${s.result === "match" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {s.result === "match" ? t("h.r_match") : t("h.r_no")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="ec-card p-6 lg:col-span-1" data-testid="recent-forms-card">
          <h3 className="font-heading font-bold text-slate-900 mb-4">{t("dash.recent_forms")}</h3>
          {data.recent_forms.length === 0 && <p className="text-sm text-slate-400">{t("dash.no_data")}</p>}
          <ul className="space-y-1.5">
            {data.recent_forms.map((f) => (
              <li key={f.id}>
                <button
                  onClick={() => downloadFile(`/forms/${f.id}/pdf`, `${f.form_number}.pdf`)}
                  data-testid={`recent-form-${f.form_number}`}
                  className="w-full text-left text-sm rounded-lg px-2 py-1.5 hover:bg-slate-100 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-800 truncate">{f.title || f.form_number}</span>
                    <Download className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#044CBC] shrink-0" />
                  </div>
                  <div className="text-xs text-slate-400">
                    {t(FORM_LABEL[f.form_type] || f.form_type)}{" · "}<span className="font-mono">{f.form_number}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Dialog open={!!client} onOpenChange={(o) => !o && setClient(null)}>
        <DialogContent className="max-w-lg" data-testid="client-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{client?.name}</DialogTitle>
          </DialogHeader>
          {client && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="ec-label">{t("common.client_id")}</span><span className="font-mono text-xs text-[#033A8C]">{client.client_id || "—"}</span></div>
                <div><span className="ec-label">{t("n.id_number")}</span><span className="text-xs">{client.id_number || "—"}</span></div>
                <div><span className="ec-label">{t("common.date")}</span><span className="text-xs">{fmtDate(client.created_at)}</span></div>
                <div><span className="ec-label">{t("k.next_review")}</span><span className="text-xs">{client.next_review || "—"}</span></div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <RiskBadge level={client.risk?.level} t={t} />
                <span className="text-xs text-slate-600 font-semibold">{t(`k.dd.${client.risk?.dd_level}`)}</span>
                <VerifyBadge verification={client.verification} t={t} />
              </div>
              <button
                onClick={() => navigate("/cumplimiento/riesgo")}
                data-testid="client-view-record"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors"
              >
                <UserCheck className="w-4 h-4" /> {t("dash.view_record")}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="search-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {detail?.loading ? t("common.loading") : detail?.query}
            </DialogTitle>
          </DialogHeader>
          {detail && !detail.loading && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="ec-label">{t("search.consult_no")}</span><span className="font-mono text-xs">{detail.consult_number}</span></div>
                <div><span className="ec-label">{t("common.date")}</span><span className="text-xs">{fmtDate(detail.created_at)}</span></div>
                <div><span className="ec-label">{t("common.user")}</span><span className="text-xs">{detail.user_name}</span></div>
                <div><span className="ec-label">{t("search.sources")}</span><span className="text-xs">{detail.sources?.join(", ")}</span></div>
              </div>
              <div className={`rounded-lg px-4 py-2.5 text-sm font-bold ${detail.result === "match" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="detail-result">
                {detail.result === "match" ? `${t("search.match")} — ${detail.match_count} ${t("search.matches_found")}` : t("search.no_match")}
              </div>
              {detail.matches?.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-64 overflow-y-auto">
                  <table className="w-full text-sm min-w-[480px]">
                    <thead>
                      <tr className="bg-[#16335F] text-white text-left">
                        <th className="px-3 py-2 text-xs font-bold uppercase">{t("search.col_list")}</th>
                        <th className="px-3 py-2 text-xs font-bold uppercase">{t("search.col_name")}</th>
                        <th className="px-3 py-2 text-xs font-bold uppercase">{t("search.col_program")}</th>
                        <th className="px-3 py-2 text-xs font-bold uppercase text-right">{t("search.col_score")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.matches.map((m, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded ${m.source === "INTERNA" ? "bg-[#044CBC] text-white" : m.source === "OFAC" ? "bg-orange-100 text-[#033A8C]" : "bg-indigo-100 text-indigo-800"}`}>{m.source === "UN" ? "CSNU" : m.source}</span></td>
                          <td className="px-3 py-2 font-semibold text-slate-800">{m.name}</td>
                          <td className="px-3 py-2 text-slate-600 text-xs">{m.program || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold">{m.score}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button
                onClick={() => downloadFile(`/sanctions/searches/${detail.id}/pdf`, `consulta_${detail.consult_number}.pdf`)}
                data-testid="detail-download-pdf"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors"
              >
                <Download className="w-4 h-4" /> {t("common.download_pdf")}
              </button>
            </div>
          )}
          {detail?.loading && <div className="py-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
